import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Heart, MessageCircle, Share2, Send, Loader2 } from 'lucide-react';

const ContentViewer = () => {
  const { contentId } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  
  // Comment state
  const [commentName, setCommentName] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchContent();
    fetchComments();
    
    // Check local storage to see if this guest user has already liked this content
    const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
    setLiked(likedPosts.includes(contentId));
  }, [contentId]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${contentId}`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/content/${contentId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleLike = async () => {
    const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
    
    try {
      if (liked) {
        await axios.delete(`${API}/content/${contentId}/like`);
        setLiked(false);
        setContent({ ...content, likes_count: Math.max(0, content.likes_count - 1) });
        
        // Remove from local storage
        localStorage.setItem('liked_posts', JSON.stringify(likedPosts.filter(id => id !== contentId)));
      } else {
        await axios.post(`${API}/content/${contentId}/like`);
        setLiked(true);
        setContent({ ...content, likes_count: content.likes_count + 1 });
        
        // Add to local storage
        likedPosts.push(contentId);
        localStorage.setItem('liked_posts', JSON.stringify(likedPosts));
      }
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();

    if (!commentName.trim() || !newComment.trim()) {
      toast.error('Please provide both your name and a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(
        `${API}/content/${contentId}/comments`,
        { 
          content_id: contentId, 
          user_name: commentName,
          text: newComment 
        }
      );
      setNewComment('');
      // Keeping commentName populated so they don't have to re-type it if they comment twice
      fetchComments();
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: content.title,
        text: content.description,
        url: window.location.href,
      });
    } catch (error) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="pt-16 min-h-screen bg-[#050505]">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="font-outfit text-3xl font-bold text-white mb-4">Content not found</h1>
          <Link to="/classes" className="text-red-500 hover:text-red-400">
            ← Back to Classes
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (content.content_type) {
      case 'video':
        return (
          <div className="relative aspect-video bg-black rounded-sm overflow-hidden protected-content" onContextMenu={(e) => e.preventDefault()}>
            <video
              src={content.file_url}
              controls
              controlsList="nodownload"
              className="w-full h-full"
              onContextMenu={(e) => e.preventDefault()}
            >
              Your browser does not support the video tag.
            </video>
            {/* Watermark */}
            <div className="absolute top-4 right-4 text-white/20 text-sm pointer-events-none font-bold tracking-widest">
              STUDY POINT
            </div>
          </div>
        );
      case 'pdf':
        return (
          <div className="relative aspect-[3/4] bg-zinc-900 rounded-sm overflow-hidden protected-content" onContextMenu={(e) => e.preventDefault()}>
            <iframe
              src={`${content.file_url}#toolbar=0`}
              className="w-full h-full"
              title={content.title}
            />
          </div>
        );
      case 'ppt':
        return (
          <div className="relative aspect-video bg-zinc-900 rounded-sm overflow-hidden protected-content" onContextMenu={(e) => e.preventDefault()}>
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(content.file_url)}`}
              className="w-full h-full"
              title={content.title}
            />
          </div>
        );
      case 'image':
        return (
          <div className="relative rounded-sm overflow-hidden protected-content" onContextMenu={(e) => e.preventDefault()}>
            <img
              src={content.file_url}
              alt={content.title}
              className="w-full max-h-[70vh] object-contain"
              draggable={false}
            />
          </div>
        );
      default:
        return <p className="text-zinc-400">Unsupported content type</p>;
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="content-viewer-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/classes" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Classes
        </Link>

        {/* Content */}
        <div className="mb-6">{renderContent()}</div>

        {/* Info */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs px-2 py-1 rounded-sm uppercase ${
                content.content_type === 'video' ? 'bg-red-500/10 text-red-500' :
                content.content_type === 'pdf' ? 'bg-blue-500/10 text-blue-500' :
                content.content_type === 'ppt' ? 'bg-orange-500/10 text-orange-500' :
                'bg-green-500/10 text-green-500'
              }`}>
                {content.content_type}
              </span>
              {content.is_paid && (
                <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-sm">
                  ₹{content.price}
                </span>
              )}
            </div>
            <h1 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-2">{content.title}</h1>
            <p className="text-zinc-400">{content.description}</p>
            <p className="text-sm text-zinc-600 mt-2">
              Posted on {new Date(content.created_at).toLocaleDateString()}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleLike}
              variant="outline"
              className={`rounded-sm ${liked ? 'border-red-500 text-red-500' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
              data-testid="like-btn"
            >
              <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-red-500' : ''}`} />
              {content.likes_count || 0}
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white rounded-sm"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Comments */}
        <div className="card-hover bg-[#121212] rounded-sm p-6">
          <h3 className="font-outfit text-lg font-bold text-white mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Comments ({comments.length})
          </h3>

          {/* Comment Form (Now Always Visible) */}
          <form onSubmit={handleComment} className="flex flex-col gap-3 mb-8">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={commentName}
                onChange={(e) => setCommentName(e.target.value)}
                placeholder="Your Name"
                className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white sm:max-w-[200px]"
                required
                data-testid="comment-name-input"
              />
              <div className="flex flex-1 gap-3">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white w-full"
                  required
                  data-testid="comment-text-input"
                />
                <Button
                  type="submit"
                  disabled={submittingComment || !newComment.trim() || !commentName.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm shrink-0"
                  data-testid="submit-comment-btn"
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </form>

          {/* Comments List */}
          {comments.length === 0 ? (
            <p className="text-zinc-500 text-center py-4">No comments yet. Be the first to comment!</p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="p-4 bg-[#0A0A0A] rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">{comment.user_name}</span>
                    <span className="text-xs text-zinc-600">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-zinc-400">{comment.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentViewer;
